import { ID, Permission, Query, Role } from "appwrite";
import {
	AppwriteClient,
	getAppwriteClient,
} from "$/modules/appwrite/lib/appwrite";
import { buckets } from "$/modules/appwrite/lib/appwrite-buckets";
import { collections } from "$/modules/appwrite/lib/appwrite-collections";
import type {
	Certificate,
	CertificateTemplate,
	ExamAttempt,
	SigningCredential,
} from "$/modules/appwrite/types/collections";
import {
	type CertificateDesign,
	certificateDesignSchema,
} from "$/modules/exam/lib/certificate-design";
import { getTranslations } from "$/modules/i18n/lib/i18n";
import { deleteFile } from "$/modules/storage/lib/storage";

const functionId =
	import.meta.env.VITE_APPWRITE_EXAM_FUNCTION_ID || "exam-manager";

export type ExamQuestion = {
	id: string;
	question: string;
	questionType: "Single" | "Multiple" | "Text" | "Info";
	options: string[];
	asset: string | null;
	assetPosition: "beforeQuestion" | "afterQuestion";
};

export type ExamStart = {
	attemptId: string;
	project: { id: string; name: string; passingScore: number };
	participantName: string;
	questions: ExamQuestion[];
};

export type ExamResult = {
	score: number;
	maxScore: number;
	percentage: number;
	passed: boolean;
};

export type ParticipantExamHistoryItem = {
	attempt: ExamAttempt;
	project: { id: string; name: string };
	certificate: Certificate | null;
};

export type AccountSuggestion = { id: string; name: string; email: string };

export type ProjectDashboardStats = {
	participants: number;
	attempts?: number;
	completedAttempts?: number;
	passRate?: number;
	averageScore?: number;
	liveSessions?: number;
	finishedSessions?: number;
	teams?: number;
};

export const executeExamAction = async <T>(
	body: Record<string, unknown>,
	loadAsyncResult?: () => Promise<T>,
): Promise<T> => {
	const functions = getAppwriteClient().functions;
	let execution = await functions.createExecution(
		functionId,
		JSON.stringify(body),
		!!loadAsyncResult,
	);
	if (loadAsyncResult) {
		const t = await getTranslations("common");
		const deadline = Date.now() + 120_000;
		while (["waiting", "processing", "scheduled"].includes(execution.status)) {
			if (Date.now() >= deadline)
				throw new Error(t("functionExecutionPending"));
			await new Promise((resolve) => setTimeout(resolve, 1000));
			execution = await functions.getExecution(functionId, execution.$id);
		}
		if (execution.status !== "completed" || execution.responseStatusCode >= 400)
			throw new Error(t("functionExecutionFailed"));
		return loadAsyncResult();
	}
	const response = JSON.parse(execution.responseBody || "{}") as
		| (T & { error?: string })
		| { data?: T; error?: string };
	if (response.error) throw new Error(response.error);
	return ("data" in response && response.data ? response.data : response) as T;
};

export const createExamInvitation = async (
	projectId: string,
	recipient: { email: string; name?: string; userId?: string },
) => {
	const { token, emailDelivery } = await executeExamAction<{
		token: string;
		emailDelivery: "sent";
	}>({
		action: "createInvitation",
		projectId,
		recipientName: recipient.name,
		recipientEmail: recipient.email,
		recipientUserId: recipient.userId,
		locale: document.documentElement.lang,
	});
	if (!token) throw new Error("Failed to create exam link.");
	return { token, emailDelivery };
};

export const createGroupExamInvitation = async (projectId: string) => {
	const { token } = await executeExamAction<{ token: string }>({
		action: "createGroupInvitation",
		projectId,
	});
	if (!token) throw new Error("Failed to create group exam link.");
	return { token };
};

export const searchAccounts = (query: string) =>
	executeExamAction<AccountSuggestion[]>({ action: "searchAccounts", query });
export const sendInvitationEmail = (
	projectId: string,
	email: string,
	kind: "exam" | "quiz",
) =>
	executeExamAction<{ sent: true }>({
		action: "sendInvitationEmail",
		projectId,
		email,
		kind,
		locale: document.documentElement.lang,
	});

export const startExam = (token: string, participantName?: string) =>
	executeExamAction<ExamStart>({ action: "start", token, participantName });

export const submitExam = (
	token: string,
	attemptId: string,
	answers: Record<string, string[]>,
) =>
	executeExamAction<ExamResult>({
		action: "submit",
		token,
		attemptId,
		answers,
	});

export type ExamAnswerDetail = {
	id: string;
	question: string;
	questionType: "Single" | "Multiple" | "Text";
	options: string[];
	submittedAnswers: string[];
	correctAnswers: string[];
	explanation: string | null;
	isCorrect: boolean;
};

export const getExamAttempts = (projectId: string) =>
	executeExamAction<ExamAttempt[]>({ action: "getExamAttempts", projectId });

export const getProjectDashboardStats = (projectId: string) =>
	executeExamAction<ProjectDashboardStats>({
		action: "getDashboardStats",
		projectId,
	});

export const recordQuizParticipation = (projectId: string, sessionId: string) =>
	executeExamAction<{ recorded: boolean }>({
		action: "recordQuizParticipation",
		projectId,
		sessionId,
	});

export const getAttemptDetails = (attemptId: string) =>
	executeExamAction<{
		attempt: ExamAttempt;
		details: { version: number; questions: ExamAnswerDetail[] } | null;
	}>({ action: "getAttemptDetails", attemptId });

export const listCertificateTemplates = async (ownerId: string) =>
	getAppwriteClient().tablesDB.listRows<CertificateTemplate>(
		AppwriteClient.databaseId,
		collections.certificateTemplates,
		[
			Query.equal("ownerId", ownerId),
			Query.orderDesc("$updatedAt"),
			Query.limit(100),
		],
	);

export const saveCertificateTemplate = async (input: {
	id?: string;
	name: string;
	orientation: CertificateDesign["orientation"];
	design: CertificateDesign;
	backgroundFileId?: string | null;
}) => {
	const user = await getAppwriteClient().account.get();
	const design = certificateDesignSchema.parse(input.design);
	const data = {
		ownerId: user.$id,
		name: input.name.trim(),
		orientation: input.orientation,
		designJson: JSON.stringify(design),
		backgroundFileId: input.backgroundFileId ?? null,
	};
	const permissions = [
		Permission.read(Role.user(user.$id)),
		Permission.update(Role.user(user.$id)),
		Permission.delete(Role.user(user.$id)),
	];
	return input.id
		? getAppwriteClient().tablesDB.updateRow<CertificateTemplate>(
				AppwriteClient.databaseId,
				collections.certificateTemplates,
				input.id,
				data,
			)
		: getAppwriteClient().tablesDB.createRow<CertificateTemplate>(
				AppwriteClient.databaseId,
				collections.certificateTemplates,
				ID.unique(),
				data,
				permissions,
			);
};

export const deleteCertificateTemplate = async (
	template: CertificateTemplate,
) => {
	if (template.backgroundFileId) {
		await deleteFile({
			bucket: buckets.certificateAssets,
			assetId: template.backgroundFileId,
		});
	}
	await getAppwriteClient().tablesDB.deleteRow(
		AppwriteClient.databaseId,
		collections.certificateTemplates,
		template.$id,
	);
};

export const getSigningCredential = () =>
	executeExamAction<SigningCredential | null>({
		action: "getSigningCredential",
	});

const arrayBufferToBase64 = (bytes: ArrayBuffer) => {
	const values = new Uint8Array(bytes);
	let binary = "";
	for (let index = 0; index < values.length; index += 0x8000) {
		binary += String.fromCharCode(...values.subarray(index, index + 0x8000));
	}
	return btoa(binary);
};

export const importSigningCredential = (file: File, passphrase: string) =>
	file.arrayBuffer().then((bytes) =>
		executeExamAction<SigningCredential>({
			action: "importSigningCredential",
			p12Base64: arrayBufferToBase64(bytes),
			passphrase,
		}),
	);

export const generateSigningCredential = (input: {
	subjectName: string;
	organization?: string;
}) =>
	executeExamAction<SigningCredential>({
		action: "generateSigningCredential",
		...input,
	});

export const issueCertificate = (input: {
	projectId: string;
	attemptId: string;
	templateId: string;
}) =>
	executeExamAction<{
		certificateId: string;
		certificateNumber: string;
		fileId: string;
		status: "issued";
	}>({ action: "issueCertificate", ...input });

export const revokeCertificate = (certificateId: string) =>
	executeExamAction<{ success: true }>({
		action: "revokeCertificate",
		certificateId,
	});
export const listCertificatesForParticipant = () =>
	executeExamAction<Certificate[]>({
		action: "listCertificatesForParticipant",
	});

export const listParticipantExamHistory = () =>
	executeExamAction<ParticipantExamHistoryItem[]>({
		action: "listParticipantExamHistory",
	});
